import React, { useState } from 'react';
import { View, Text, Pressable, Modal, ScrollView, StyleSheet, Platform } from 'react-native';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { getDisplayName } from '../../shared/utils/user';
import { Ionicons } from '@expo/vector-icons';
import { Badge } from '../ui/Badge';

export interface AssigneeSelectorProps {
  value: number[];
  onSelect: (userIds: number[]) => void;
  availablePartners: Array<{ id: number; email: string; display_name?: string }>;
  size?: 'sm' | 'md' | 'lg';
  maxDisplay?: number;
}

export function AssigneeSelector({ 
  value, 
  onSelect,
  availablePartners,
  size = 'md',
  maxDisplay = 2
}: AssigneeSelectorProps) {
  const styles = useStyles();
  const { tokens, utils } = styles;
  const { isMobile } = useResponsive();
  const [modalVisible, setModalVisible] = useState(false);
  const selectorStyles = makeSelectorStyles(tokens, utils, isMobile, size);

  const selectedPartners = availablePartners.filter(p => value.includes(p.id));
  const remainingCount = selectedPartners.length - maxDisplay;

  const handleToggle = (userId: number) => {
    if (value.includes(userId)) {
      onSelect(value.filter(id => id !== userId));
    } else {
      onSelect([...value, userId]);
    }
  };

  return (
    <>
      <Pressable
        onPress={() => setModalVisible(true)}
        style={({ pressed }) => [
          selectorStyles.container,
          pressed && selectorStyles.pressed,
        ]}
      >
        {selectedPartners.length === 0 ? (
          <View style={selectorStyles.emptyState}>
            <Ionicons 
              name="person-add-outline" 
              size={16} 
              color={addColorOpacity(tokens.color.onSurface, 0.5)} 
            />
            <Text style={selectorStyles.emptyText}>
              Assign
            </Text>
          </View>
        ) : (
          <View style={selectorStyles.badgesContainer}>
            {selectedPartners.slice(0, maxDisplay).map((partner) => (
              <Badge
                key={partner.id}
                label={getDisplayName(partner)}
                variant="primary"
                size={size === 'sm' ? 'xs' : 'sm'}
              />
            ))}
            {remainingCount > 0 && (
              <Badge
                label={`+${remainingCount}`}
                variant="subtle"
                size={size === 'sm' ? 'xs' : 'sm'}
              />
            )}
          </View>
        )}
      </Pressable>

      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setModalVisible(false)}
      >
        <Pressable
          style={selectorStyles.modalOverlay}
          onPress={() => setModalVisible(false)}
        >
          <Pressable
            style={selectorStyles.modalContent}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={selectorStyles.modalHeader}>
              <Text style={selectorStyles.modalTitle}>Assign Partners</Text>
              <Pressable
                onPress={() => setModalVisible(false)}
                style={selectorStyles.closeButton}
              >
                <Ionicons name="close" size={24} color={tokens.color.onSurface} />
              </Pressable>
            </View>

            <ScrollView style={selectorStyles.optionsList}>
              {availablePartners.map((partner) => {
                const isSelected = value.includes(partner.id);

                return (
                  <Pressable
                    key={partner.id}
                    onPress={() => handleToggle(partner.id)}
                    style={({ pressed }) => [
                      selectorStyles.option,
                      isSelected && selectorStyles.optionSelected,
                      pressed && selectorStyles.optionPressed,
                    ]}
                  >
                    <View style={selectorStyles.optionContent}>
                      <View style={[
                        selectorStyles.checkbox,
                        isSelected && selectorStyles.checkboxSelected,
                      ]}>
                        {isSelected && (
                          <Ionicons 
                            name="checkmark" 
                            size={16} 
                            color={tokens.color.onPrimary} 
                          />
                        )}
                      </View>
                      <View style={selectorStyles.optionInfo}>
                        <Text style={[
                          selectorStyles.optionName,
                          isSelected && selectorStyles.optionNameSelected,
                        ]}>
                          {getDisplayName(partner)}
                        </Text>
                        <Text style={selectorStyles.optionEmail}>
                          {partner.email}
                        </Text>
                      </View>
                    </View>
                  </Pressable>
                );
              })}
            </ScrollView>

            <View style={selectorStyles.modalFooter}>
              <Text style={selectorStyles.selectedCount}>
                {value.length} {value.length === 1 ? 'partner' : 'partners'} selected
              </Text>
              <Pressable
                onPress={() => setModalVisible(false)}
                style={selectorStyles.doneButton}
              >
                <Text style={selectorStyles.doneButtonText}>Done</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </>
  );
}

function makeSelectorStyles(
  tokens: ReturnType<typeof useStyles>['tokens'],
  utils: ReturnType<typeof useStyles>['utils'],
  isMobile: boolean,
  size: AssigneeSelectorProps['size']
) {
  return StyleSheet.create({
    container: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      minHeight: size === 'sm' ? 32 : 40,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    pressed: {
      opacity: 0.7,
    },
    emptyState: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.xs,
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      borderRadius: utils.radius.md,
      backgroundColor: addColorOpacity(tokens.color.onSurface, 0.05),
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderStyle: 'dashed',
    },
    emptyText: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    badgesContainer: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.xs,
      alignItems: 'center',
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: isMobile ? utils.spacing.md : utils.spacing.xl,
    },
    modalContent: {
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      width: '100%',
      maxWidth: isMobile ? '100%' : 400,
      maxHeight: '80%',
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...Platform.select({
        web: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        } as any,
      }),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.1,
      shadowRadius: 16,
      elevation: 4,
    },
    modalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    modalTitle: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
    },
    closeButton: {
      padding: utils.spacing.xs,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    optionsList: {
      maxHeight: 400,
    },
    option: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: utils.spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: addColorOpacity(tokens.color.border, 0.5),
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    optionSelected: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.05),
    },
    optionPressed: {
      backgroundColor: addColorOpacity(tokens.color.primary, 0.1),
    },
    optionContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: utils.spacing.md,
      flex: 1,
    },
    checkbox: {
      width: 24,
      height: 24,
      borderRadius: utils.radius.sm,
      borderWidth: 2,
      borderColor: tokens.color.border,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkboxSelected: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    optionInfo: {
      flex: 1,
    },
    optionName: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.medium,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    optionNameSelected: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.semibold,
    },
    optionEmail: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      marginTop: 2,
    },
    modalFooter: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: utils.spacing.lg,
      borderTopWidth: 1,
      borderTopColor: tokens.color.border,
    },
    selectedCount: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.7),
      fontFamily: utils.typography.body.fontFamily,
    },
    doneButton: {
      paddingHorizontal: utils.spacing.lg,
      paddingVertical: utils.spacing.sm,
      borderRadius: utils.radius.md,
      backgroundColor: tokens.color.primary,
      ...Platform.select({
        web: {
          cursor: 'pointer',
        } as any,
      }),
    },
    doneButtonText: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onPrimary,
      fontFamily: utils.typography.body.fontFamily,
    },
  });
}

